import { setOptions, importLibrary } from "@googlemaps/js-api-loader";
import {
    getIP,
    setIpLocation,
    getIpLocation,
    getLanguage,
    isSafari
} from "../components/global";
import { Functions } from "./Functions";
import { HeartBank } from "./HeartBank";
import { doc, updateDoc } from "firebase/firestore";
import { fs } from "../firebase.ts";

interface FormattedLocation {
    place_id: string;
    formatted_address: string;
    lat: number;
    lng: number;
    //TODO: store: address_components and others??
}

// TODO: later create org with this data
export interface Place {
    id: string; // google place id // later use org id
    lat: number;
    lng: number;
    displayName: string;
    formattedAddress: string;
    locality: string; // city
    region: string; // state/province
    country: string; // for flag
    //photo: string;
}

import Strings from "../models/Strings.json";
import { add } from "dexie";
const strings: any = Strings;

const lang = getLanguage();
setOptions({
    key: "AIzaSyD9JlpRf6AoihLS4JkvG8bMT9Mg6pvnvH0",
    v: "weekly",
    libraries: ["core", "geocoding", "places"],
    language: strings.language[lang],
    region: strings.region[lang]
});

export class Location {
    private heartbank = new HeartBank();
    private functions = new Functions();

    private watchId: number | null = null;
    private isGranted = false; // one time per reload
    private isPrompted = false; // one time per reload
    private isPrompted2 = false; // one time per reload

    private readonly DEGREES_PER_METER_LAT = 0.000008983;
    private meters = 150; // about 0.1 miles // to search and cache
    private addressCache = new Map<string, FormattedLocation>();
    private cachedPlaces = new Map<string, Place[]>(); //TODO: later store in database to save money
    // 0.05 mile (≈80 meters) — still safe for most cases
    // 0.02 mile (≈30 meters) — only if outdoors; indoors GPS is too noisy

    public location: google.maps.LatLngLiteral | null = null; // current location
    public cachedLocation: string = ""; // like cacheKey
    public readonly longBeach = {
        lat: 33.77005,
        lng: -118.193741,
        address: "Long Beach, California, USA",
        region: "Long Beach, CA",
        country: "US"
    };

    public map: google.maps.Map | null = null;
    private infoWindow: google.maps.InfoWindow | null = null;
    public marker: google.maps.marker.AdvancedMarkerElement | null = null; // user
    public mintMarkers: google.maps.marker.AdvancedMarkerElement[] = [];
    public mineMarkers: google.maps.marker.AdvancedMarkerElement[] = [];

    constructor() {
        this.initMap(); // preload
        this.setLocation();
    }

    private async init() {
        await this.initMap(); // ensure exists
        await this.getCurrentLocation(); // try to get current location
    }

    // https://www.npmjs.com/package/@googlemaps/js-api-loader
    private async initMap() {
        await importLibrary("core");
        await importLibrary("maps");
        await importLibrary("marker");
        await importLibrary("geometry");
        await importLibrary("geocoding");
        await importLibrary("places");
    }

    private async setLocation() {
        const ip = await getIP();
        await setIpLocation(ip);
    }

    public async renderMap(
        mapElement: HTMLElement,
        color: "light" | "dark" | "system" | null = null
    ) {
        let colorScheme;
        const scheme = color || localStorage.getItem("colorScheme");
        switch (scheme) {
            case "light":
                colorScheme = google.maps.ColorScheme.LIGHT;
                break;
            case "dark":
                colorScheme = google.maps.ColorScheme.DARK;
                break;
            case "system":
                colorScheme = google.maps.ColorScheme.FOLLOW_SYSTEM;
                break;
        }
        await this.init();
        const location = this.location || this.longBeach;

        this.map = new google.maps.Map(mapElement, {
            center: location,
            zoom: 2.5, // initial
            mapId: "cb6a20406bc5ef5f761bbc93",
            colorScheme,
            mapTypeId: google.maps.MapTypeId.TERRAIN,
            tilt: 45,
            zoomControl: false,
            fullscreenControl: false,
            disableDefaultUI: true,
            mapTypeControl: true,
            streetViewControl: true,

            // --- Configure Control Positions ---
            zoomControlOptions: {
                position: google.maps.ControlPosition.RIGHT_TOP
            },
            mapTypeControlOptions: {
                //style: google.maps.MapTypeControlStyle.HORIZONTAL_BAR, // Optional: change style
                position: google.maps.ControlPosition.TOP_CENTER
            },
            streetViewControlOptions: {
                position: google.maps.ControlPosition.BOTTOM_CENTER
            },
            fullscreenControlOptions: {
                position: google.maps.ControlPosition.BOTTOM_LEFT
            }
        });

        this.infoWindow = new google.maps.InfoWindow();
        const content = `<hb-me uid="${this.heartbank.user?.uid || ""}" class="p-8 pt-2"></hb-me>`;

        const pin = new google.maps.marker.PinElement({
            background: "red",
            borderColor: "orangered",
            glyph: "❤️", //❤
            glyphColor: "white",
            scale: 2.0
        });

        this.marker = new google.maps.marker.AdvancedMarkerElement({
            position: location,
            map: this.map,
            content: pin.element,
            title: "Title"
        });

        this.marker.addListener("gmp-click", () => {
            this.marker!.position = this.location || this.longBeach;
            this.panToUser();
            this.infoWindow!.setContent(content);
            this.infoWindow!.open(this.map, this.marker);
        });

        //console.log(this.map);
        return this.map;
    }

    private panToUser() {
        this.map!.panTo(this.location || this.longBeach);
        if (this.map!.getZoom()! < 8) this.map!.setZoom(12);
        //google.maps.event.trigger(this.marker!, "gmp-click");
    }

    /**
     * Finds nearby places for a given coordinate, utilizing a grid-based cache.
     * @param center The geographic coordinate to search around.
     * @returns A promise that resolves to an array of nearby places.
     */
    public async getNearbyPlaces(isReset = false): Promise<Place[]> {
        await this.init();
        const location = getIpLocation();
        const coordinate =
            location.latitude && location.longitude
                ? { lat: location.latitude, lng: location.longitude }
                : null;
        if (!this.location) this.location = coordinate || this.longBeach;

        const cacheKey = this._createCacheKey(this.location);
        if (!isReset && this.cachedPlaces.has(cacheKey)) {
            console.log("Returning places from cache...");
            return this.cachedPlaces.get(cacheKey)!;
        }

        console.log("Fetching new places from API...");
        const places = await this._fetchAndCachePlaces(this.location, cacheKey);
        return places;
    }

    /**
     * Creates a cache key based on a geographic grid.
     * @param center The coordinate to snap to the grid.
     * @returns A unique string key for the grid cell.
     */
    private _createCacheKey(center: google.maps.LatLngLiteral): string {
        const gridSizeDegrees = this.DEGREES_PER_METER_LAT * this.meters;

        // Snap the coordinates to a grid to create a consistent cache key for a given area.
        const gridLat =
            Math.round(center.lat / gridSizeDegrees) * gridSizeDegrees;
        const gridLng =
            Math.round(center.lng / gridSizeDegrees) * gridSizeDegrees;

        // Using toFixed to ensure consistent key format.
        return `grid_${gridLat.toFixed(4)}_${gridLng.toFixed(4)}`;
    }

    /**
     * Fetches places from the Google Maps API, transforms them, and caches the result.
     * @param center The coordinate for the API search.
     * @param cacheKey The key under which to store the results.
     * @returns A promise that resolves to an array of places, or an empty array on error.
     */
    private async _fetchAndCachePlaces(
        center: google.maps.LatLngLiteral,
        cacheKey: string
    ): Promise<Place[]> {
        // Fields are not specified in searchNearby like they are in searchByText.
        // You would typically use the place ID from this result to make a
        // follow-up Place Details request for more info like formattedAddress.
        // For simplicity, we'll use the data available.
        // NOTE: The modern `Place.searchNearby()` (v2) does support a field mask.
        // Let's assume you're using the newer library as in your example.

        const modernRequest: google.maps.places.SearchNearbyRequest = {
            locationRestriction: {
                center,
                radius: this.meters
            },
            fields: [
                "id",
                "displayName",
                "location",
                "formattedAddress",
                "addressComponents"
                //"photos"
            ],
            maxResultCount: 10,
            language: strings.lang[lang]
            // You can add 'type' or 'keyword' here for more specific searches
            // e.g., keyword: "restaurant"
        };

        try {
            // Using Place.searchNearby is more appropriate for this use case.
            const { places } =
                await google.maps.places.Place.searchNearby(modernRequest);

            const formattedPlaces = this._transformPlaces(places);
            this.cachedPlaces.set(cacheKey, formattedPlaces);

            if (formattedPlaces.length) {
                console.log("New cached places:", formattedPlaces);
            } else {
                console.log("No places found for the current location.");
            }

            return formattedPlaces;
        } catch (error) {
            console.error("Place search failed:", error);
            // Return an empty array on failure to prevent crashes in the calling code.
            return [];
        }
    }

    /**
     * Transforms Google API place results into our application's Place format.
     * @param places An array of places from the Google Maps API.
     * @returns A formatted array of Place objects.
     */
    private _transformPlaces(places: google.maps.places.Place[]): Place[] {
        return places.map(
            (place): Place => ({
                id: place.id ?? `fallback-id-${Math.random()}`, // Ensure ID is never null
                displayName: place.displayName ?? "Unnamed Place",
                formattedAddress: place.formattedAddress ?? "Unknown Address",
                locality:
                    place.addressComponents?.find((component) =>
                        component.types.includes("locality")
                    )?.longText || "Unknown City",
                region:
                    place.addressComponents?.find((component) =>
                        component.types.includes("administrative_area_level_1")
                    )?.shortText || "Unknown Region",
                country:
                    place.addressComponents?.find((component) =>
                        component.types.includes("country")
                    )?.shortText || "Unknown Country",
                lat: place.location?.lat() ?? 0,
                lng: place.location?.lng() ?? 0
                // photo:
                //     place.photos && place.photos.length > 0
                //         ? place.photos[0].getURI({ maxWidth: 400 })
                //         : undefined
            })
        );
    }

    ///// geolocation

    public async getCurrentLocation() {
        if ("geolocation" in navigator) {
            const geolocation = await navigator.permissions.query({
                name: "geolocation"
            });
            this.handleGeolocation(geolocation);
            geolocation.onchange = () => this.handleGeolocation(geolocation); // does not work on ios
        } else alert("Please allow geolocation to share your location.");
    }

    // chrome
    private handleGeolocation(geolocation: PermissionStatus) {
        switch (geolocation.state) {
            case "prompt": // ios only here: TODO: fix confirm not asked prior to prompt
                if (
                    localStorage.getItem("isGranted") === "true" ||
                    this.isPrompted
                )
                    this.getCurrentPosition();
                else {
                    this.isPrompted = true;
                    this.isGranted = navigator.onLine
                        ? confirm(
                              "If you share your location, nearby Zero Heroes can thank you!\n\n(Always Allow to stop this message from reappearing)"
                          )
                        : false;
                    if (this.isGranted) this.getCurrentPosition();
                }
                break;
            case "granted":
                this.setGranted(true);
                break;
            case "denied":
                this.setGranted(false);
                break;
            default: // error?
                this.setGranted(false);
        }
    }

    private setGranted(isGranted: boolean) {
        if (isGranted) {
            localStorage.setItem("isGranted", "true");
            this.watchCurrentPosition();
        } else {
            localStorage.setItem("isGranted", "false");
            if (this.watchId) navigator.geolocation.clearWatch(this.watchId);
        }
    }

    private getCurrentPosition() {
        navigator.geolocation.getCurrentPosition(
            (position) => {
                this.location = {
                    lat: position.coords.latitude,
                    lng: position.coords.longitude
                };
                //this.map!.setCenter(this.location);
                //this.map!.setZoom(12);
                this.handleGeolocation2(true);
            },
            (error: any) => {
                this.handleGeolocation2(false);
                // if (error.code === error.PERMISSION_DENIED) {
                // } else {
                //     console.error("Geolocation error: ", error.message);
                // }
            }
        );
    }

    // safari
    private handleGeolocation2(isGranted: boolean) {
        if (isSafari) {
            if (isGranted) {
                this.setGranted(true);
            } else {
                this.setGranted(false);
                if (this.isGranted && !this.isPrompted2) {
                    this.isPrompted2 = true;
                    if (navigator.onLine)
                        alert(
                            "Please allow geolocation to share your location."
                        );
                }
            }
        }
    }

    private watchCurrentPosition() {
        if (!this.watchId) {
            this.watchId = navigator.geolocation.watchPosition(
                async (position) => {
                    //const { latitude, longitude } = position.coords;
                    this.location = {
                        lat: position.coords.latitude,
                        lng: position.coords.longitude
                    };

                    const cacheKey = this._createCacheKey(this.location);
                    if (this.cachedLocation !== cacheKey) {
                        this.cachedLocation = cacheKey;
                        const address = await this.geocode(
                            this.location,
                            cacheKey
                        );
                        //console.log(address);

                        if (address && this.heartbank.user) {
                            const userDocRef = doc(
                                fs,
                                "users",
                                this.heartbank.user.uid
                            );
                            await updateDoc(userDocRef, {
                                // TODO: later store lastUpdated
                                address: address.formatted_address,
                                latitude: address.lat,
                                longitude: address.lng
                            });
                            // TODO: later store in rtdb for location history...
                        }

                        if (address && this.heartbank.isAdmin())
                            this.functions.follow(address.formatted_address);
                    }
                },
                (error: any) => {
                    this.handleGeolocation2(false);
                    // if (error.code === error.PERMISSION_DENIED) {
                    // } else {
                    //     console.error("Error watching position: ", error.message);
                    // }
                },
                {
                    enableHighAccuracy: true, // optional: request high-accuracy if possible
                    maximumAge: 0, // optional: don't use cached position
                    timeout: Infinity // optional: no timeout
                }
            );
        }
    }

    private async geocode(
        coordinate: google.maps.LatLngLiteral,
        cacheKey: string
    ): Promise<FormattedLocation | null> {
        if (this.addressCache.has(cacheKey)) {
            const cachedLocation = this.addressCache.get(
                cacheKey
            ) as FormattedLocation;
            cachedLocation.lat = coordinate.lat;
            cachedLocation.lng = coordinate.lng;
            console.log("Using cached location address...", cachedLocation);
            return cachedLocation;
        }

        try {
            const geocoder = new google.maps.Geocoder();
            const request: google.maps.GeocoderRequest = {
                location: coordinate,
                language: strings.language[lang]
            };
            const response = await geocoder.geocode(request);

            if (response.results && response.results.length > 0) {
                // Use the first, most specific result
                const result = response.results[0];
                //console.log(result);

                const location: FormattedLocation = {
                    place_id: result.place_id,
                    formatted_address: result.formatted_address,
                    lat: coordinate.lat, //result.geometry.location.lat(),
                    lng: coordinate.lng //result.geometry.location.lng()
                };

                this.addressCache.set(cacheKey, location);
                return location;
            } else {
                console.warn(
                    "Geocoding failed: No results found for the location.",
                    coordinate
                );
                return null;
            }
        } catch (error) {
            console.error("Geocoding API call failed:", error);
            return null;
        }
    }
}
